import { useEffect, useRef } from 'react';

import { useAppSelector } from '@verantos-dev/app-state-base';
import { selectResearchAssistantModel } from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';
import {
  emitSubscribeToCohort,
  emitResubscribeToCohort,
  emitUnsubscribeFromCohort,
} from '../emitters/connectionEvents';

export const useResearchAssistantConnectionListeners = () => {
  const hasSubscribed = useRef(false);
  const { user } = useAuth();
  const { projectId, cohortId, cohortVersionId, data } = useCohortBuilderData();
  const { socket: currentSocket, cleanupSocket } = useResearchAssistantSocketContext();
  const model = useAppSelector(selectResearchAssistantModel);

  const isOwner =
    !!data?.data.createdBy.userId &&
    [user.auth0Id, user.userId].includes(data?.data.createdBy.userId);
  const isDataPresent = !!data?.data;

  // Socket connection lifecycle
  useEffect(() => {
    if (!currentSocket) return;

    console.info('Setting up research assistant socket listeners.');

    currentSocket?.on('connect', () => {
      console.info('Connected to research assistant socket:', currentSocket.id);
    });

    currentSocket?.on('disconnect', (reason) => {
      console.info('Disconnected from research assistant socket:', reason);
    });

    currentSocket.io.on('reconnect', () => {
      if (currentSocket && projectId && cohortId && cohortVersionId && model) {
        emitResubscribeToCohort(currentSocket, projectId, cohortId, cohortVersionId, model, user);
      }
    });
    // Cleanup on unmount
    return () => {
      console.info('Cleaning up research assistant socket listeners and state.');

      emitUnsubscribeFromCohort(projectId || '', cohortId || '', cohortVersionId, model);
    };
  }, [currentSocket, projectId, cohortId, model, cohortVersionId, user]);

  // Cohort room subscription
  useEffect(() => {
    if (currentSocket && projectId && cohortId && model && isDataPresent) {
      console.info(
        `Subscribing to cohort room: project ${projectId}, cohort ${cohortId}, model ${model}`
      );

      if (!hasSubscribed.current) {
        hasSubscribed.current = true;
        emitSubscribeToCohort(
          currentSocket,
          projectId,
          cohortId,
          cohortVersionId,
          model,
          isOwner,
          user
        );
      } else {
        emitResubscribeToCohort(currentSocket, projectId, cohortId, cohortVersionId, model, user);
      }
    }
  }, [projectId, cohortId, model, isOwner, cohortVersionId, isDataPresent, currentSocket, user]);

  useEffect(() => {
    return () => {
      console.info('Cleaning up research assistant socket listeners and state.');
      cleanupSocket();
    };
  }, [cleanupSocket]);
};
